import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { ulid } from "ulid";
import type { ImportModel } from "@recipevault/shared";
import { Button } from "@/ui/button.js";
import { JobProgressCard } from "../../features/imports/JobProgressCard.js";
import {
  useImport,
  type UseImportResult,
} from "../../features/imports/useImport.js";
import { deleteImport, submitImport } from "../../features/imports/api.js";
import { getRecipe } from "../../features/recipes/api.js";
import { db } from "../../sync/db.js";

export function ImportDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const result: UseImportResult = useImport(id);
  const { data: job, status, error } = result;

  // Was the job already `done` the first time we observed it? If so the user is
  // viewing a completed job from history — don't auto-redirect (that creates a
  // back-button trap: back → here → re-redirect). We only auto-redirect when the
  // job transitions to `done` while this view is mounted (the import happy path).
  const wasDoneOnMountRef = useRef<boolean | null>(null);
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (!job) return;
    if (wasDoneOnMountRef.current === null) {
      wasDoneOnMountRef.current = job.status === "done";
    }
    if (
      job.status === "done" &&
      job.recipeId &&
      !wasDoneOnMountRef.current &&
      !redirectedRef.current
    ) {
      const recipeId = job.recipeId;
      const jobId = job.id;
      redirectedRef.current = true;
      void (async () => {
        // The worker created the recipe server-side only — nothing has synced it
        // into Dexie yet, so useRecipe would render "Recipe not found". Pull it
        // into the cache before navigating so the editor renders immediately.
        try {
          const { recipe } = await getRecipe(recipeId);
          await db.recipes.put(recipe);
        } catch {
          // Best-effort: if the fetch fails the editor's own load/sync will recover;
          // don't block the navigation the user is expecting.
        }
        // replace:true so Back skips this terminal job view and avoids the
        // redirect trap (back → /imports/{id} → effect re-redirects). Photo
        // imports land a hidden `pending` draft → the source-pinned review
        // screen; url/text land a live recipe → the plain editor.
        void navigate(
          job.kind === "photo"
            ? `/imports/${encodeURIComponent(jobId)}/review`
            : `/recipes/${encodeURIComponent(recipeId)}/edit?from=import&jobId=${encodeURIComponent(jobId)}`,
          { replace: true },
        );
      })();
    }
  }, [job, navigate]);

  if (!id) return <p className="p-6 text-destructive">Missing import id.</p>;
  if (status === "loading" && !job)
    return <p className="p-6 text-muted-foreground">Loading…</p>;
  if (status === "error" && !job) {
    return (
      <p className="p-6 text-destructive">
        Failed to load: {error?.message ?? "unknown error"}
      </p>
    );
  }
  if (!job) return <p className="p-6 text-destructive">Import not found.</p>;

  const isTerminal = ["done", "failed", "over_cap"].includes(job.status);
  const isFailed = job.status === "failed";

  async function onRetryWithStronger(): Promise<void> {
    if (!job) return;
    const nextModel: ImportModel = job.model === "haiku" ? "sonnet" : "haiku";
    try {
      const req =
        job.kind === "url"
          ? { kind: "url" as const, url: job.url!, model: nextModel }
          : { kind: "text" as const, text: job.text!, model: nextModel };
      const { jobId } = await submitImport(req, ulid());
      await navigate(`/imports/${encodeURIComponent(jobId)}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Retry failed");
    }
  }

  async function onDelete(): Promise<void> {
    if (!job) return;
    try {
      await deleteImport(job.id);
      await navigate("/imports");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    }
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-6 space-y-4">
      <h1 className="text-2xl font-semibold">Import progress</h1>
      <JobProgressCard job={job} />

      {isFailed && job.kind === "photo" && (
        // Photo retry re-captures on Sonnet 5 — never downgrades to Haiku (the
        // url/text "retry with a different model" path must not apply here).
        <Button
          variant="default"
          onClick={() => {
            void navigate("/imports/photo");
          }}
        >
          Re-take photo
        </Button>
      )}

      {isFailed && job.kind !== "photo" && (
        <Button
          variant="default"
          onClick={() => {
            void onRetryWithStronger();
          }}
        >
          {job.model === "haiku"
            ? "Retry with stronger model (Sonnet)"
            : "Try again"}
        </Button>
      )}

      {job.status === "over_cap" && (
        <Button
          variant="secondary"
          onClick={() => {
            void navigate("/settings/household");
          }}
        >
          View cap settings
        </Button>
      )}

      {isTerminal && (
        <Button
          variant="ghost"
          onClick={() => {
            void onDelete();
          }}
        >
          Delete from history
        </Button>
      )}
    </div>
  );
}
